import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Flag, Inbox, LoaderCircle, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function AdminModeration() {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/moderation").then((r) => setData(r.data))
      .catch(() => toast.error("Impossible de charger la modération."));
  }, []);
  useEffect(() => { load(); }, [load]);

  const reactivate = async (id) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/users/${id}`, { suspended: false });
      toast.success("Compte réactivé.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action impossible.");
    } finally {
      setBusyId(null);
    }
  };

  if (!data) return <div className="flex justify-center py-16"><LoaderCircle className="h-6 w-6 animate-spin text-slate-300" /></div>;

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="page-admin-moderation">
      <div>
        <h1 className="flex items-center gap-2.5 font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">
          <Flag className="h-7 w-7 text-bleu" /> Modération
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Comptes suspendus ({data.counts.suspended}) et signalements en attente ({data.counts.reports_new}).
        </p>
      </div>

      <section className="space-y-2.5" data-testid="admin-moderation-suspended">
        <h2 className="flex items-center gap-2 text-sm font-bold text-slate-900">
          <ShieldAlert className="h-4 w-4 text-rouge" /> Comptes suspendus
        </h2>
        {data.suspended.length === 0 ? (
          <div className="card-integra p-8 text-center">
            <p className="text-sm font-bold text-slate-700">Aucun compte suspendu</p>
          </div>
        ) : data.suspended.map((u) => (
          <div key={u.id} className="card-integra flex flex-wrap items-center gap-3 p-4" data-testid="admin-moderation-suspended-row">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-slate-900">{u.name || "—"}</p>
              <p className="text-xs text-slate-500">{u.email}{u.city && ` · ${u.city}`}</p>
            </div>
            <button disabled={busyId === u.id} onClick={() => reactivate(u.id)}
              className="flex items-center gap-1.5 rounded-full border border-emerald-200 px-3.5 py-1.5 text-[11px] font-bold text-emerald-700 transition-colors hover:bg-emerald-50"
              data-testid="admin-moderation-reactivate">
              {busyId === u.id ? <LoaderCircle className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
              Réactiver
            </button>
          </div>
        ))}
      </section>

      <section className="space-y-2.5" data-testid="admin-moderation-reports">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-sm font-bold text-slate-900">
            <Inbox className="h-4 w-4 text-bleu" /> Signalements & suggestions à traiter
          </h2>
          <Link to="/admin/suggestions" className="text-[11px] font-bold text-bleu hover:underline" data-testid="admin-moderation-goto-support">
            Ouvrir le support →
          </Link>
        </div>
        {data.reports.length === 0 ? (
          <div className="card-integra p-8 text-center">
            <p className="text-sm font-bold text-slate-700">Aucun signalement en attente</p>
          </div>
        ) : data.reports.map((r) => (
          <div key={r.id} className="card-integra p-4" data-testid="admin-moderation-report-row">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{r.category}</p>
            <p className="mt-1 line-clamp-2 text-xs text-slate-600">{r.message}</p>
            <p className="mt-1.5 text-[11px] text-slate-400">
              {r.user ? `${r.user.name || r.user.email}` : "Utilisateur inconnu"}
              {" · "}{new Date(r.created_at).toLocaleDateString("fr-FR")}
            </p>
          </div>
        ))}
      </section>
    </div>
  );
}
